/** The catalog item that holds the weight per foot of each mill. */
export const WEIGHT_ITEM_CODE = "WEIGHT";

export const SECTION_SPEC_FILE_KIND = {
	MILL_DRAWING: "Mill Drawing",
	DIMENSIONS_REFERENCE: "Dimensions Reference",
} as const;

export type SectionSpecFileKind =
	(typeof SECTION_SPEC_FILE_KIND)[keyof typeof SECTION_SPEC_FILE_KIND];

export const SECTION_SPEC_STATUS = {
	DRAFT: "D",
	SCHEDULED: "S",
	PUBLISHED: "P",
} as const;

type SectionSpecStatus =
	(typeof SECTION_SPEC_STATUS)[keyof typeof SECTION_SPEC_STATUS];

export const SECTION_SPEC_LIFECYCLE = {
	CURRENT: "CURRENT",
	DRAFT: "DRAFT",
	PUBLISHED: "PUBLISHED",
	SCHEDULED: "SCHEDULED",
} as const;

type SectionSpecLifecycle =
	(typeof SECTION_SPEC_LIFECYCLE)[keyof typeof SECTION_SPEC_LIFECYCLE];

export interface SectionSpec {
	ProfileSpecId: number;
	SectionId: number;
	AssetId: number;
	Rev: number | null;
	SubRev?: number | null;
	EffectiveDate: string | null;
	StatusCode: SectionSpecStatus;
	LifecycleCode: SectionSpecLifecycle;
	LifecycleLabel: string;
	SectionName: string;
	SectionDescription: string | null;
	PrintNo: string | null;
	Area: number | null;
	TargetLbsPerFoot: number | null;
	MinLbsPerFoot: number | null;
	MaxLbsPerFoot: number | null;
	LegDeltaMax: number | null;
	MillDrawingUploadFileId: number | null;
	MillDrawingFileName: string | null;
	DimensionsReferenceUploadFileId: number | null;
	DimensionsReferenceFileName: string | null;
	Comments: string | null;
	CreatedBy: string;
	CreatedAt: string;
	InspectionCount: number;
}

export interface SectionSpecVersion {
	ProfileSpecId: number;
	SectionId: number;
	Rev: number | null;
	SubRev?: number | null;
	EffectiveDate: string | null;
	StatusCode: SectionSpecStatus;
	LifecycleCode: SectionSpecLifecycle;
	LifecycleLabel: string;
	InspectionCount: number;
}

export interface SectionSpecMetadata {
	SectionId: number;
	AssetId: number;
	CurrentPublishedProfileSpecId: number | null;
	NextScheduledPublishedProfileSpecId: number | null;
	NextScheduledPublishedRev: number | null;
	NextScheduledPublishedSubRev?: number | null;
	NextScheduledPublishedEffectiveDate: string | null;
	ActiveDraftProfileSpecId: number | null;
	ActiveDraftRev: number | null;
	NewRevNumber: number;
	PreviousEffectiveDate: Date | null;
	LatestConfirmedProfileSpecId: number | null;
	LatestInspectedAt: Date | null;
}

export interface SectionSpecPublication {
	revision?: number;
	effectiveDate: Date;
}

export const SECTION_SPEC_CONFIRMATION_ACTION = {
	IMAGES_UPDATED: "IMAGES_UPDATED",
	REVISION_CONFIRMED: "REVISION_CONFIRMED",
} as const;

export type SectionSpecConfirmationAction =
	(typeof SECTION_SPEC_CONFIRMATION_ACTION)[keyof typeof SECTION_SPEC_CONFIRMATION_ACTION];

export interface SectionSpecConfirmationResult {
	ActionCode: SectionSpecConfirmationAction;
	ProfileSpecId: number;
	Rev: number | null;
	SubRev: number | null;
}

export interface SectionSpecItem {
	ProfileSpecId: number | null;
	ProfileItemId: number;
	ProfileCategoryId: number;
	CategoryName: string;
	CategorySortOrder: number;
	Code: string;
	Name: string;
	DisplayName: string | null;
	ValueTypeCode: string;
	SortOrder: number;
	IsActive: boolean;
	IsDegrees?: boolean;
	Applies: boolean;
	Ltl: number | null;
	Nominal: number | null;
	Utl: number | null;
	MeasuredInspectionCount: number;
}

export interface SectionSpecDetail {
	spec: SectionSpec | null;
	items: SectionSpecItem[];
	weightProfileItemId: number | null;
	metadata: SectionSpecMetadata | null;
	versions: SectionSpecVersion[];
}

export type SpecItemRow = Omit<
	SectionSpecItem,
	"ProfileSpecId" | "MeasuredInspectionCount"
>;

export interface SpecItemPayload {
	ProfileItemId: number;
	IsDegrees?: boolean;
	Ltl: number | null;
	Nominal: number | null;
	Utl: number | null;
}
